if (!window.Maximize?.loadedScript?.includes("store-locator.js")) {
  if (window.Maximize?.loadedScript) {
    window.Maximize.loadedScript.push("store-locator.js");
  }

  const GEO_SESSION_KEY = "a2z_store_locator_geo_prompted";

  const PROMPT_SESSION_KEY = "a2z_store_locator_prompt_dismissed";

  // Canadian postal prefixes (A1…A1A1A1 — any length 2–6), and US ZIP (12345 / 12345-6789).
  const POSTAL_PATTERN =
    /^(?:[A-Z]\d(?:[A-Z](?:\d(?:[A-Z]\d?)?)?)?|\d{5}(?:-?\d{4})?)$/;

  /** Distance (km) beyond which we show the "no store nearby" notice. */
  const FAR_MARKET_KM = 350;

  /**
   * After geocoding, force certain cities to a market instead of nearest-by-distance.
   * Keys are normalized city names (lowercase, alphanumeric).
   */
  const GEOCODE_CITY_MARKET_OVERRIDES = {
    london: "windsor",
  };

  /**
   * Dev/prod Shopify Markets use different handles for the same cities
   * (e.g. halifax vs halifax-location). Coordinates file may use either.
   */
  const MARKET_HANDLE_ALIASES = {
    halifax: ["halifax", "halifax-location"],
    "halifax-location": ["halifax-location", "halifax"],
    mississauga: ["mississauga", "mississauga-location"],
    "mississauga-location": ["mississauga-location", "mississauga"],
    windsor: ["windsor", "windsor-location"],
    "windsor-location": ["windsor-location", "windsor"],
  };

  let storeLocatorRegistered = false;
  let googleMapsLoadPromise = null;

  function getStoreLocatorRoot() {
    return document.querySelector(".store-locator-popup__container");
  }

  function readJsonScript(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error("[store-locator] failed to parse", id, err);
      return null;
    }
  }

  function resolveMarketCoordinates(handle, curatedCoords) {
    if (!curatedCoords || !handle) return null;
    const candidates = MARKET_HANDLE_ALIASES[handle] || [handle];
    for (const key of candidates) {
      const coords = curatedCoords[key];
      if (coords && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lng))) {
        return { lat: Number(coords.lat), lng: Number(coords.lng), matchedKey: key };
      }
    }
    // Last resort: city-token match against coordinate keys.
    const token = String(handle).split("-")[0];
    if (token) {
      for (const [key, coords] of Object.entries(curatedCoords)) {
        if (
          key === token ||
          key.startsWith(`${token}-`) ||
          key.includes(token)
        ) {
          if (coords && Number.isFinite(Number(coords.lat)) && Number.isFinite(Number(coords.lng))) {
            return { lat: Number(coords.lat), lng: Number(coords.lng), matchedKey: key };
          }
        }
      }
    }
    return null;
  }

  function registerStoreLocator() {
    if (storeLocatorRegistered || !window.Alpine) return;
    storeLocatorRegistered = true;

    Alpine.data("xStoreLocator", () => ({
      show: false,
      active: null,
      currentSelected: false,
      selectedStore: "",
      postalInput: "",
      postalError: false,
      /** @type {null|'format'|'lookup'|'config'|'no_market'} */
      postalErrorReason: null,
      postalFallback: false,
      postalLoading: false,
      geoLoading: false,
      geoError: false,
      geoResultOpen: false,
      geoResultStoreName: "",
      filterMarketHandle: null,
      pendingPostal: null,
      closestBlockId: null,
      visibleStoreIds: null,
      marketsByHandle: {},
      stores: [],
      sectionId: "",
      _marketsLoadPromise: null,

      init() {
        const root = this.$el;
        this.sectionId = root.dataset.sectionId || "";
        // Keep location accordions collapsed until the shopper expands one.
        const firstBlockId = root.dataset.activeBlockId || null;
        this.active = null;
        const autoSelectFirstStore = root.dataset.autoSelectFirst === "true";
        this.autoPromptEnabled = root.dataset.autoPrompt !== "false";

        const stores = readJsonScript(`StoreLocatorStores-${this.sectionId}`);
        this.stores = Array.isArray(stores) ? stores : [];

        const apiKey = this.googleMapsApiKey();
        console.log("[store-locator] init", {
          sectionId: this.sectionId,
          stores: this.stores,
          autoSelectFirstStore,
        });
        console.log("[store-locator][google] config", {
          hasApiKey: Boolean(apiKey),
          apiKey: this.maskApiKey(apiKey),
          mapsAlreadyLoaded: Boolean(window.google?.maps?.Geocoder),
        });
        if (!apiKey) {
          console.warn(
            "[store-locator][google] no API key configured — zip geocoding will fail until Theme settings → A2Z Store locator is set"
          );
        }

        const saved = JSON.parse(localStorage.getItem("currentStoreSelected") || "null");
        const currentSelectedStore = saved?.currentSelected
          ? saved.currentSelected
          : autoSelectFirstStore
            ? firstBlockId
            : false;
        const currentCheckbox = document.getElementById(
          `Checkbox__${this.sectionId}-${currentSelectedStore}`
        );
        if (currentCheckbox) {
          this.currentSelected = currentSelectedStore;
          this.selectedStore = currentCheckbox.dataset.storeName;
          Alpine.store("xStoreMap").currentStoreSelected = this.selectedStore;
          const postal = saved?.postal || "";
          if (postal) {
            this.pendingPostal = this.normalizePostal(postal);
            Alpine.store("xStoreMap").currentPostal = this.formatPostalDisplay(postal);
          }
          localStorage.setItem(
            "currentStoreSelected",
            JSON.stringify({
              selectedStore: this.selectedStore,
              currentSelected: this.currentSelected,
              postal: this.pendingPostal || saved?.postal || "",
            })
          );
          document.dispatchEvent(
            new CustomEvent("maximize:store-changed", { detail: { store: this.selectedStore } })
          );
        } else if (saved?.postal) {
          this.pendingPostal = this.normalizePostal(saved.postal);
          Alpine.store("xStoreMap").currentPostal = this.formatPostalDisplay(saved.postal);
        }

        this.loadMarketData();

        const storeLocatorContentEl = this.$el.querySelector(".store-locator__content");
        const tabletMediaQuery = window.matchMedia(`(min-width: ${MIN_DEVICE_WIDTH.tablet}px)`);

        const handleMediaChange = (e) => {
          if (!storeLocatorContentEl || !this.show) return;
          if (e.matches) {
            storeLocatorContentEl.style.height = `${storeLocatorContentEl.getBoundingClientRect().height}px`;
          } else {
            storeLocatorContentEl.style.height = "";
          }
        };

        if (storeLocatorContentEl) {
          tabletMediaQuery.addEventListener("change", handleMediaChange);
        }

        document.addEventListener(`${STORE_MAP_EVENT.openPopup}`, () => {
          this.openStoreLocatorPopup();
          setTimeout(() => handleMediaChange(tabletMediaQuery), 300);
        });

        if (window.Shopify?.designMode) {
          document.addEventListener("shopify:section:select", (event) => {
            if (event.target.classList.contains("section-store-locator")) {
              this.openStoreLocatorPopup();
            }
          });
        }

        this.maybeAutoOpen();
      },

      maybeAutoOpen() {
        const skip = (reason, extra) =>
          console.log("[store-locator][auto-prompt] skipped —", reason, extra || "");

        if (window.Shopify?.designMode) return skip("design mode");
        if (!this.autoPromptEnabled) return skip("disabled in section settings");
        if (window.Maximize?.isB2B) return skip("B2B customer");
        if (this.hasSavedStoreSelection()) return skip("store already selected");
        try {
          if (sessionStorage.getItem(PROMPT_SESSION_KEY)) {
            return skip("already dismissed this session", { key: PROMPT_SESSION_KEY });
          }
        } catch (_) {}

        console.log("[store-locator][auto-prompt] opening — no location selected");
        this.openStoreLocatorPopup();
      },

      normalizePostal(postal) {
        return String(postal || "")
          .toUpperCase()
          .replace(/\s+/g, "");
      },

      formatPostalDisplay(postal) {
        const n = this.normalizePostal(postal);
        // Canadian display: space after the FSA once the local portion starts (4–6 chars).
        if (/^[A-Z]\d[A-Z]/.test(n) && n.length > 3) {
          return `${n.slice(0, 3)} ${n.slice(3)}`;
        }
        return n;
      },

      isPostalLike(postal) {
        return POSTAL_PATTERN.test(this.normalizePostal(postal));
      },

      postalErrorMessage() {
        const root = this.$el;
        const reason = this.postalErrorReason || "format";
        const map = {
          format: root?.dataset?.zipErrorFormat,
          lookup: root?.dataset?.zipErrorLookup,
          config: root?.dataset?.zipErrorConfig,
          no_market: root?.dataset?.zipErrorNoMarket,
        };
        return (
          map[reason] ||
          map.format ||
          "Please enter a valid zip / postal code."
        );
      },

      setPostalError(reason) {
        this.postalError = true;
        this.postalErrorReason = reason || "format";
        this.postalFallback = false;
      },

      clearPostalError() {
        this.postalError = false;
        this.postalErrorReason = null;
      },

      fallbackMarketHandle() {
        const preferred = window.Maximize?.catalog?.fallbackMarketHandle || null;
        return (
          this.resolveExistingMarketHandle(preferred) ||
          Object.keys(this.marketsByHandle || {})[0] ||
          null
        );
      },

      googleMapsApiKey() {
        return String(window.Maximize?.googleMapsApiKey || "").trim();
      },

      /** Mask key for console logs (shows prefix/suffix only). */
      maskApiKey(apiKey) {
        const key = String(apiKey || "");
        if (!key) return "(empty)";
        if (key.length <= 8) return `${key.slice(0, 2)}…`;
        return `${key.slice(0, 4)}…${key.slice(-4)} (len=${key.length})`;
      },

      formatGeocoderError(err) {
        if (!err) return { message: "unknown error" };
        return {
          name: err.name || "GeocoderError",
          message: err.message || String(err),
          code: err.code,
          status: err.status || err.code,
        };
      },

      normalizeCityKey(value) {
        return String(value || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, " ")
          .trim();
      },

      extractGeocodeCity(result) {
        const components = result?.address_components || [];
        const byType = (type) =>
          components.find((c) => Array.isArray(c.types) && c.types.includes(type))?.long_name ||
          null;

        return (
          byType("locality") ||
          byType("postal_town") ||
          byType("administrative_area_level_3") ||
          byType("neighborhood") ||
          null
        );
      },

      /**
       * If the geocoded city has an override, return that market handle.
       * Also allows theme config: Maximize.catalog.geocodeCityMarketOverrides
       */
      /**
       * Map a preferred/aliased handle onto a real LOCATION market from markets JSON.
       */
      resolveExistingMarketHandle(preferredHandle) {
        if (!preferredHandle) return null;
        if (this.marketsByHandle[preferredHandle]) return preferredHandle;
        const aliases = MARKET_HANDLE_ALIASES[preferredHandle] || [preferredHandle];
        for (const alias of aliases) {
          if (this.marketsByHandle[alias]) return alias;
        }
        const token = String(preferredHandle).split("-")[0];
        if (token) {
          const match = Object.keys(this.marketsByHandle).find(
            (h) => h === token || h.startsWith(`${token}-`) || h.includes(token)
          );
          if (match) return match;
        }
        return null;
      },

      resolveMarketOverride(location) {
        const city = location?.city || this.extractGeocodeCity(location?.result);
        const cityKey = this.normalizeCityKey(city);
        if (!cityKey) return null;

        const configured = window.Maximize?.catalog?.geocodeCityMarketOverrides || {};
        const overrides = { ...GEOCODE_CITY_MARKET_OVERRIDES, ...configured };
        const preferred = overrides[cityKey] || null;
        const handle = this.resolveExistingMarketHandle(preferred);

        if (handle) {
          console.log("[store-locator][google] city market override matched", {
            city,
            cityKey,
            preferred,
            handle,
            formattedAddress: location?.formattedAddress,
          });
        }

        return handle;
      },

      normalizeMatch(value) {
        return String(value || "")
          .toLowerCase()
          .replace(/&amp;/g, "&")
          .replace(/&#39;/g, "'")
          .replace(/&quot;/g, '"')
          .replace(/[^a-z0-9]+/g, " ")
          .trim();
      },

      marketTokens(handle) {
        const market = this.marketsByHandle[handle];
        const name = this.normalizeMatch(market?.name);
        const normalizedHandle = this.normalizeMatch(market?.handle || handle);
        return [name, normalizedHandle, ...(normalizedHandle ? normalizedHandle.split(" ") : [])].filter(
          (t) => t && t.length > 2 && !["location", "region", "pos"].includes(t)
        );
      },

      storeMatchesMarket(storeName, marketHandle) {
        if (!marketHandle) return true;
        const s = this.normalizeMatch(storeName);
        if (!s) return false;
        return this.marketTokens(marketHandle).some((token) => s === token || s.includes(token));
      },

      isStoreVisible(blockId) {
        if (!this.filterMarketHandle || !this.visibleStoreIds) return true;
        return this.visibleStoreIds.includes(blockId);
      },

      loadMarketData() {
        if (this._marketsLoadPromise) return this._marketsLoadPromise;

        const marketsUrl = window.Maximize?.catalog?.marketsUrl;
        const coordsUrl = window.Maximize?.catalog?.marketCoordinatesUrl;

        this._marketsLoadPromise = (async () => {
          try {
            const fetches = [
              marketsUrl
                ? fetch(marketsUrl).then((r) => (r.ok ? r.json() : { markets: [] }))
                : Promise.resolve({ markets: [] }),
              coordsUrl ? fetch(coordsUrl).then((r) => (r.ok ? r.json() : null)) : Promise.resolve(null),
            ];
            const [marketsPayload, curatedCoords] = await Promise.all(fetches);
            const byHandle = {};
            (marketsPayload?.markets || []).forEach((m) => {
              if (m?.handle && m.type === "LOCATION") {
                const fromMarketLat = Number(m.lat);
                const fromMarketLng = Number(m.lng);
                const curated = resolveMarketCoordinates(m.handle, curatedCoords);
                const lat = Number.isFinite(fromMarketLat) ? fromMarketLat : curated?.lat ?? null;
                const lng = Number.isFinite(fromMarketLng) ? fromMarketLng : curated?.lng ?? null;
                byHandle[m.handle] = {
                  name: m.name,
                  handle: m.handle,
                  lat: Number.isFinite(lat) ? lat : null,
                  lng: Number.isFinite(lng) ? lng : null,
                  coordsKey: curated?.matchedKey || null,
                };
              }
            });
            this.marketsByHandle = byHandle;
            console.log("[store-locator] fetched markets", {
              marketsByHandle: this.marketsByHandle,
              coordsUrl,
              coordsKeys: curatedCoords ? Object.keys(curatedCoords) : [],
            });
          } catch (err) {
            console.error("[store-locator] market data load failed", err);
          }
          return this.marketsByHandle;
        })();

        return this._marketsLoadPromise;
      },

      loadGoogleMapsApi(apiKey) {
        if (window.google?.maps?.Geocoder) {
          console.log("[store-locator][google] Maps JS already loaded", {
            hasGeocoder: true,
            mapsVersion: window.google?.maps?.version || null,
          });
          return Promise.resolve();
        }
        if (googleMapsLoadPromise) {
          console.log("[store-locator][google] Maps JS load already in flight — awaiting");
          return googleMapsLoadPromise;
        }

        const startedAt = performance.now();
        const LOAD_TIMEOUT_MS = 12000;
        console.log("[store-locator][google] loading Maps JS API", {
          key: this.maskApiKey(apiKey),
          host: window.location?.host,
          protocol: window.location?.protocol,
          timeoutMs: LOAD_TIMEOUT_MS,
        });

        googleMapsLoadPromise = new Promise((resolve, reject) => {
          const callbackName = "__a2zGoogleMapsReady";
          let settled = false;
          const previous = window[callbackName];
          const finish = (fn, value) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            fn(value);
          };

          window[callbackName] = () => {
            if (typeof previous === "function") {
              try {
                previous();
              } catch (_) {}
            }
            delete window[callbackName];
            const elapsedMs = Math.round(performance.now() - startedAt);
            if (window.google?.maps?.Geocoder) {
              console.log("[store-locator][google] Maps JS ready", {
                elapsedMs,
                hasGeocoder: true,
                mapsVersion: window.google?.maps?.version || null,
              });
              finish(resolve);
            } else {
              console.error("[store-locator][google] Maps JS callback fired but Geocoder missing", {
                elapsedMs,
                hasGoogle: Boolean(window.google),
                hasMaps: Boolean(window.google?.maps),
              });
              finish(reject, new Error("Google Maps Geocoder unavailable"));
            }
          };

          const timeoutId = window.setTimeout(() => {
            delete window[callbackName];
            console.error("[store-locator][google] Maps JS load timed out", {
              elapsedMs: LOAD_TIMEOUT_MS,
              key: this.maskApiKey(apiKey),
            });
            finish(reject, new Error("Google Maps script load timed out"));
          }, LOAD_TIMEOUT_MS);

          const script = document.createElement("script");
          script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}&v=weekly`;
          script.async = true;
          script.defer = true;
          script.onerror = () => {
            delete window[callbackName];
            console.error("[store-locator][google] Maps JS script failed to load", {
              elapsedMs: Math.round(performance.now() - startedAt),
              key: this.maskApiKey(apiKey),
              hint: "Check API key, Maps JavaScript API enabled, and HTTP referrer restrictions",
            });
            finish(reject, new Error("Google Maps script failed to load"));
          };
          document.head.appendChild(script);
          console.log("[store-locator][google] Maps JS script tag injected");
        }).catch((err) => {
          googleMapsLoadPromise = null;
          throw err;
        });

        return googleMapsLoadPromise;
      },

      async geocodePostal(postal, apiKey) {
        const display = this.formatPostalDisplay(postal);
        // Bias strongly to Canada — full postals like "L5T 2J3" are often misread without this.
        const request = {
          address: `${display}, Canada`,
          region: "ca",
          componentRestrictions: { country: "CA" },
        };
        const startedAt = performance.now();

        console.log("[store-locator][google] geocode start", {
          postalRaw: postal,
          postalDisplay: display,
          request,
          key: this.maskApiKey(apiKey),
        });

        await this.loadGoogleMapsApi(apiKey);
        const geocoder = new google.maps.Geocoder();

        try {
          const response = await geocoder.geocode(request);
          const results = response?.results || [];
          const elapsedMs = Math.round(performance.now() - startedAt);

          console.log("[store-locator][google] geocode response", {
            postalDisplay: display,
            elapsedMs,
            resultCount: results.length,
            results: results.slice(0, 5).map((r) => ({
              formattedAddress: r.formatted_address,
              types: r.types,
              partialMatch: r.partial_match || false,
              locationType: r.geometry?.location_type,
              lat: typeof r.geometry?.location?.lat === "function"
                ? r.geometry.location.lat()
                : r.geometry?.location?.lat,
              lng: typeof r.geometry?.location?.lng === "function"
                ? r.geometry.location.lng()
                : r.geometry?.location?.lng,
            })),
          });

          if (!results.length) {
            console.warn("[store-locator][google] geocode ZERO_RESULTS", {
              postalDisplay: display,
              elapsedMs,
            });
            return null;
          }

          const result =
            results.find((r) => r.types?.includes("postal_code")) ||
            results.find((r) => r.types?.includes("postal_code_prefix")) ||
            results[0];
          const loc = result?.geometry?.location;
          if (!loc) {
            console.warn("[store-locator][google] geocode result missing geometry.location", {
              postalDisplay: display,
              pickedTypes: result?.types,
            });
            return null;
          }

          const lat = typeof loc.lat === "function" ? loc.lat() : Number(loc.lat);
          const lng = typeof loc.lng === "function" ? loc.lng() : Number(loc.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            console.warn("[store-locator][google] geocode lat/lng not finite", { lat, lng });
            return null;
          }

          const city = this.extractGeocodeCity(result);

          console.log("[store-locator][google] geocode lat/lng", {
            postal: display,
            lat,
            lng,
            city,
            formattedAddress: result.formatted_address,
          });
          console.log("[store-locator][google] geocode OK", {
            postalDisplay: display,
            elapsedMs,
            lat,
            lng,
            city,
            formattedAddress: result.formatted_address,
            types: result.types,
            locationType: result.geometry?.location_type,
            pickedFromResultCount: results.length,
          });

          return {
            lat,
            lng,
            city,
            formattedAddress: result.formatted_address,
            result,
          };
        } catch (err) {
          const elapsedMs = Math.round(performance.now() - startedAt);
          console.error("[store-locator][google] geocode FAILED", {
            postalDisplay: display,
            elapsedMs,
            key: this.maskApiKey(apiKey),
            error: this.formatGeocoderError(err),
            hint:
              "REQUEST_DENIED usually means bad key, Geocoding/Maps JS API not enabled, or referrer blocked",
          });
          return null;
        }
      },

      selectClosestStore(marketHandle) {
        const matching = this.stores.filter((store) => this.storeMatchesMarket(store.name, marketHandle));
        console.log("[store-locator] selectClosestStore", {
          marketHandle,
          stores: this.stores,
          tokens: this.marketTokens(marketHandle),
          matching,
        });

        this.visibleStoreIds = matching.map((store) => store.id);
        this.currentSelected = false;
        this.selectedStore = "";
        this.closestBlockId = null;

        if (!matching.length) {
          console.warn("[store-locator] no store matched market", marketHandle);
          return false;
        }

        const best = matching[0];
        this.currentSelected = best.id;
        this.selectedStore = best.name;
        // Select the closest store, but do not auto-expand its details accordion.
        this.closestBlockId = best.id;
        console.log("[store-locator] auto-selected store", best);
        return true;
      },

      async applyPostalFilter() {
        const apiKey = this.googleMapsApiKey();
        console.log("[store-locator][google] applyPostalFilter start", {
          postalInput: this.postalInput,
          hasApiKey: Boolean(apiKey),
          apiKey: this.maskApiKey(apiKey),
          mapsAlreadyLoaded: Boolean(window.google?.maps?.Geocoder),
          geoLoading: this.geoLoading,
        });

        // Never block zip search on background geolocation — that made the field
        // look "unavailable" for up to ~35s on first open / PDP visits.
        if (this.postalLoading) {
          console.log("[store-locator][google] applyPostalFilter skipped — already loading");
          return;
        }

        const key = this.normalizePostal(this.postalInput);

        if (!this.isPostalLike(key)) {
          this.setPostalError("format");
          console.warn("[store-locator][google] invalid postal format", {
            raw: this.postalInput,
            normalized: key,
          });
          return;
        }

        if (!apiKey) {
          this.setPostalError("config");
          console.error(
            "[store-locator][google] missing API key — Theme settings → A2Z Store locator → Google Maps API key"
          );
          return;
        }

        this.postalLoading = true;
        this.clearPostalError();
        this.postalFallback = false;
        this.geoError = false;
        const startedAt = performance.now();

        try {
          await this.loadMarketData();

          const location = await this.geocodePostal(key, apiKey);
          if (!location) {
            this.setPostalError("lookup");
            console.warn("[store-locator][google] applyPostalFilter aborted — geocode returned null", {
              key,
              elapsedMs: Math.round(performance.now() - startedAt),
            });
            return;
          }

          console.log("[store-locator][google] geocoded coordinates for postal", {
            postal: key,
            lat: location.lat,
            lng: location.lng,
            city: location.city,
            formattedAddress: location.formattedAddress,
          });

          const overrideHandle = this.resolveMarketOverride(location);
          const closest = this.findClosestMarketHandle(location.lat, location.lng);
          const handle = overrideHandle || closest?.handle || this.fallbackMarketHandle();
          console.log("[store-locator][google] applyPostalFilter market resolution", {
            key,
            city: location.city,
            overrideHandle,
            closest: closest
              ? {
                  handle: closest.handle,
                  name: closest.name,
                  distanceKm: Number(closest.distanceKm?.toFixed?.(2) ?? closest.distanceKm),
                }
              : null,
            handle,
            usedOverride: Boolean(overrideHandle),
            usedFallback: !overrideHandle && !closest?.handle && Boolean(handle),
          });

          if (!handle) {
            this.setPostalError("no_market");
            console.warn("[store-locator][google] no market found for geocoded postal", key);
            return;
          }

          this.postalFallback =
            !overrideHandle &&
            closest?.distanceKm != null &&
            closest.distanceKm > FAR_MARKET_KM;
          this.filterMarketHandle = handle;
          this.pendingPostal = key;
          // Keep header "Pickup at" in sync with the zip the customer just searched.
          try {
            Alpine.store("xStoreMap").currentPostal = this.formatPostalDisplay(key);
          } catch (_) {}
          const selected = this.selectClosestStore(handle);
          console.log("[store-locator][google] applyPostalFilter done", {
            ok: selected,
            key,
            city: location.city,
            handle,
            usedOverride: Boolean(overrideHandle),
            selectedStore: this.selectedStore,
            postalFallback: this.postalFallback,
            elapsedMs: Math.round(performance.now() - startedAt),
          });
        } catch (err) {
          this.setPostalError("lookup");
          console.error("[store-locator][google] applyPostalFilter failed", {
            key,
            error: this.formatGeocoderError(err),
            elapsedMs: Math.round(performance.now() - startedAt),
          });
        } finally {
          this.postalLoading = false;
        }
      },

      clearPostalFilter() {
        this.postalInput = "";
        this.clearPostalError();
        this.postalFallback = false;
        this.postalLoading = false;
        this.geoError = false;
        this.filterMarketHandle = null;
        this.pendingPostal = null;
        this.closestBlockId = null;
        this.visibleStoreIds = null;
      },

      onPostalInput() {
        this.clearPostalError();
        this.postalFallback = false;
        this.geoError = false;
        const typed = this.normalizePostal(this.postalInput);
        if (!typed) {
          // Customer cleared the field — don't keep a stale pickup zip on confirm.
          this.pendingPostal = null;
          return;
        }
        if (this.isPostalLike(typed)) {
          this.pendingPostal = typed;
        }
      },

      /**
       * Postal shown as "Pickup at …" after confirming a store.
       * Prefer what the customer currently typed; never keep a stale zip
       * from a previous search when the field was cleared.
       */
      resolveCommittedPostal() {
        const typed = this.normalizePostal(this.postalInput);
        if (typed && this.isPostalLike(typed)) {
          return typed;
        }
        if (this.pendingPostal) {
          return this.normalizePostal(this.pendingPostal);
        }
        return "";
      },

      haversineKm(lat1, lng1, lat2, lng2) {
        const toRad = (deg) => (deg * Math.PI) / 180;
        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
        return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      },

      formatGeoError(err) {
        if (!err) return { message: "unknown error" };
        const codeMap = {
          1: "PERMISSION_DENIED",
          2: "POSITION_UNAVAILABLE",
          3: "TIMEOUT",
        };
        return {
          name: err.name || "GeolocationError",
          message: err.message || String(err),
          code: err.code,
          codeLabel: err.code != null ? codeMap[err.code] || `CODE_${err.code}` : undefined,
        };
      },

      requestBrowserPositionOnce(options, attemptLabel) {
        return new Promise((resolve, reject) => {
          console.log("[store-locator][geo] requesting navigator.geolocation", {
            attempt: attemptLabel,
            supported: Boolean(navigator.geolocation),
            secureContext: window.isSecureContext,
            protocol: window.location?.protocol,
            options,
          });

          if (!navigator.geolocation) {
            reject(new Error("geolocation unsupported"));
            return;
          }

          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const coords = pos.coords;
              console.log("[store-locator][geo] browser position received", {
                attempt: attemptLabel,
                latitude: coords.latitude,
                longitude: coords.longitude,
                accuracyMeters: coords.accuracy,
                altitude: coords.altitude,
                heading: coords.heading,
                speed: coords.speed,
                timestamp: pos.timestamp,
              });
              resolve(coords);
            },
            (err) => {
              console.warn("[store-locator][geo] browser position failed", {
                attempt: attemptLabel,
                ...this.formatGeoError(err),
              });
              reject(err);
            },
            options
          );
        });
      },

      /**
       * Prefer a cached/network fix first, then retry with high accuracy on timeout.
       */
      async getBrowserPosition() {
        const attempts = [
          {
            label: "1/cached-or-network",
            options: { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 },
          },
          {
            label: "2/high-accuracy",
            options: { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
          },
        ];

        let lastError = null;
        for (const attempt of attempts) {
          try {
            return await this.requestBrowserPositionOnce(attempt.options, attempt.label);
          } catch (err) {
            lastError = err;
            const code = err?.code;
            // Don't retry if the user denied permission
            if (code === 1) break;
            console.log("[store-locator][geo] retrying with next strategy…", {
              failedAttempt: attempt.label,
              error: this.formatGeoError(err),
            });
          }
        }

        throw lastError || new Error("geolocation failed");
      },

      findClosestMarketHandle(lat, lng) {
        const ranked = Object.values(this.marketsByHandle || {})
          .map((market) => {
            if (market.lat == null || market.lng == null) {
              return {
                handle: market.handle,
                name: market.name,
                lat: market.lat,
                lng: market.lng,
                distanceKm: null,
                skipped: "missing lat/lng",
              };
            }
            const distanceKm = this.haversineKm(lat, lng, market.lat, market.lng);
            return {
              handle: market.handle,
              name: market.name,
              lat: market.lat,
              lng: market.lng,
              distanceKm,
              skipped: null,
            };
          })
          .sort((a, b) => {
            if (a.distanceKm == null) return 1;
            if (b.distanceKm == null) return -1;
            return a.distanceKm - b.distanceKm;
          });

        console.log("[store-locator][geo] distance ranking vs user position", {
          user: { lat, lng },
          markets: ranked,
        });

        const best = ranked.find((m) => m.distanceKm != null);
        return best
          ? { handle: best.handle, name: best.name, distanceKm: best.distanceKm, ranked }
          : null;
      },

      hasSavedStoreSelection() {
        try {
          const saved = JSON.parse(localStorage.getItem("currentStoreSelected") || "null");
          return Boolean(saved?.selectedStore);
        } catch (_) {
          return false;
        }
      },

      /**
       * Ask for browser location and auto-select the closest market store.
       * @param {{ force?: boolean, quiet?: boolean }} [opts]
       *   force — user clicked "Use my location" (always try)
       *   quiet — session auto-prompt (no error UI on deny)
       */
      async detectNearbyStore({ force = false, quiet = false } = {}) {
        const source = force ? "button:use-my-location" : "auto:first-open";
        console.log("[store-locator][geo] detectNearbyStore start", {
          source,
          force,
          quiet,
          geoLoading: this.geoLoading,
          filterMarketHandle: this.filterMarketHandle,
          hasSavedStore: this.hasSavedStoreSelection(),
        });

        if (this.geoLoading) {
          console.log("[store-locator][geo] skipped — already loading");
          return false;
        }

        if (!force) {
          try {
            if (sessionStorage.getItem(GEO_SESSION_KEY)) {
              console.log("[store-locator][geo] skipped — already prompted this session", {
                key: GEO_SESSION_KEY,
              });
              return false;
            }
          } catch (_) {}
          if (this.hasSavedStoreSelection()) {
            console.log("[store-locator][geo] skipped — saved store already exists");
            return false;
          }
          if (this.filterMarketHandle) {
            console.log("[store-locator][geo] skipped — filter already active", {
              filterMarketHandle: this.filterMarketHandle,
            });
            return false;
          }
          try {
            sessionStorage.setItem(GEO_SESSION_KEY, "1");
          } catch (_) {}
        }

        this.geoLoading = true;
        this.geoError = false;
        this.clearPostalError();
        this.postalFallback = false;

        try {
          await this.loadMarketData();
          const marketHandles = Object.keys(this.marketsByHandle || {});
          console.log("[store-locator][geo] markets loaded for distance check", {
            count: marketHandles.length,
            handles: marketHandles,
            marketsByHandle: this.marketsByHandle,
            stores: this.stores,
          });

          const coords = await this.getBrowserPosition();
          const closest = this.findClosestMarketHandle(coords.latitude, coords.longitude);
          if (!closest?.handle) {
            console.warn("[store-locator][geo] no closest market — missing coords on markets?", {
              user: { lat: coords.latitude, lng: coords.longitude },
              marketsByHandle: this.marketsByHandle,
            });
            if (!quiet) this.geoError = true;
            return false;
          }

          console.log("[store-locator][geo] selecting closest market/store", {
            source,
            user: { lat: coords.latitude, lng: coords.longitude, accuracyMeters: coords.accuracy },
            closest: {
              handle: closest.handle,
              name: closest.name,
              distanceKm: Number(closest.distanceKm?.toFixed?.(2) ?? closest.distanceKm),
            },
          });
          this.filterMarketHandle = closest.handle;
          const selected = this.selectClosestStore(closest.handle);
          console.log("[store-locator][geo] selectClosestStore result", {
            ok: selected,
            selectedStore: this.selectedStore,
            currentSelected: this.currentSelected,
            closestBlockId: this.closestBlockId,
            visibleStoreIds: this.visibleStoreIds,
          });
          if (force && selected && this.selectedStore) {
            this.geoResultStoreName = this.selectedStore;
            this.geoResultOpen = true;
            console.log("[store-locator][geo] showing result dialog", {
              store: this.geoResultStoreName,
              distanceKm: Number(closest.distanceKm?.toFixed?.(2) ?? closest.distanceKm),
            });
          }
          try {
            sessionStorage.setItem(GEO_SESSION_KEY, "1");
          } catch (_) {}
          return true;
        } catch (err) {
          console.warn("[store-locator][geo] detectNearbyStore failed", {
            source,
            error: this.formatGeoError(err),
          });
          if (!quiet) this.geoError = true;
          return false;
        } finally {
          this.geoLoading = false;
          console.log("[store-locator][geo] detectNearbyStore end", {
            source,
            geoError: this.geoError,
            filterMarketHandle: this.filterMarketHandle,
            selectedStore: this.selectedStore,
          });
        }
      },

      /** Once per session on first locator open with no saved store. */
      async trySuggestFromGeolocation() {
        await this.detectNearbyStore({ force: false, quiet: true });
      },

      closeGeoResult() {
        this.geoResultOpen = false;
      },

      openStoreLocatorPopup() {
        this.show = true;
        // Always start with store detail rows collapsed when the modal opens.
        this.active = null;
        // Recover from a stuck loading state (e.g. prior Maps load hang / interrupted geo).
        if (this.postalLoading) {
          console.warn("[store-locator] clearing stuck postalLoading on open");
          this.postalLoading = false;
        }
        try {
          Alpine.store("xMaximizePopup")?.handleOpen?.();
          Alpine.store("xMaximizeDrawer").activeDrawerElement = window.Maximize?.isShowPopupOverlay;
        } catch (_) {
          /* stores may be missing in some previews */
        }
        console.log("[store-locator] openStoreLocatorPopup show=true", {
          postalLoading: this.postalLoading,
          geoLoading: this.geoLoading,
          hasApiKey: Boolean(this.googleMapsApiKey()),
        });
        // Background only — must not disable zip entry (see applyPostalFilter).
        this.trySuggestFromGeolocation();
      },

      closeStoreLocatorPopup() {
        this.geoResultOpen = false;
        if (this.currentSelected === false) {
          localStorage.removeItem("currentStoreSelected");
          Alpine.store("xStoreMap").currentStoreSelected = "";
          Alpine.store("xStoreMap").currentPostal = "";
          document.dispatchEvent(new CustomEvent("maximize:store-changed", { detail: { store: "" } }));
        }
        this.show = false;
        try {
          Alpine.store("xMaximizePopup")?.handleClose?.();
        } catch (_) {}

        if (!this.hasSavedStoreSelection()) {
          try {
            sessionStorage.setItem(PROMPT_SESSION_KEY, "1");
            console.log("[store-locator][auto-prompt] dismissed for this session");
          } catch (_) {}
        }
      },

      toggleSelected(el, currentSelected) {
        this.currentSelected = this.currentSelected == currentSelected ? false : currentSelected;
        this.selectedStore = this.currentSelected ? el.dataset.storeName : "";
      },

      async setSelectedStore() {
        if (!this.currentSelected) return;
        const postal = this.resolveCommittedPostal();
        const postalDisplay = postal ? this.formatPostalDisplay(postal) : "";
        const storeName = this.selectedStore;
        this.pendingPostal = postal || null;

        console.log("[store-locator] setSelectedStore committing postal", {
          storeName,
          postalInput: this.postalInput,
          pendingPostal: this.pendingPostal,
          committedPostal: postal,
          postalDisplay,
        });

        // Commit store first so inventory + pricing syncs read the new location
        // (not a stale in-flight ensure for the previous store).
        Alpine.store("xStoreMap").currentStoreSelected = storeName;
        Alpine.store("xStoreMap").currentPostal = postalDisplay;
        localStorage.setItem(
          "currentStoreSelected",
          JSON.stringify({
            selectedStore: storeName,
            currentSelected: this.currentSelected,
            postal,
          })
        );
        document.dispatchEvent(
          new CustomEvent("maximize:store-changed", {
            detail: { store: storeName, postal: postalDisplay },
          })
        );
        this.closeStoreLocatorPopup();

        // 1) Adjust cart qty for the new store's inventory
        try {
          if (window.Maximize?.locationInventory?.reconcileCartForStoreChange) {
            console.log("[store-locator] validating cart inventory for store", storeName);
            await window.Maximize.locationInventory.reconcileCartForStoreChange(storeName);
          }
        } catch (err) {
          console.error("[store-locator] cart inventory check failed", err);
        }

        // 2) Write active_location + re-render cart sections so drawer/page show
        //    location-pricing transform prices before (and after) reload.
        try {
          if (window.Maximize?.activeLocation?.ensure) {
            await window.Maximize.activeLocation.ensure(storeName, { forceRefresh: true });
          }
        } catch (err) {
          console.error("[store-locator] active_location sync before refresh failed", err);
        }
        window.location.reload();
      },

      isItemPartiallyVisible(el) {
        const itemEl = el.closest(".store-locator__content__item") || el;
        const listItemsEl =
          window.innerWidth >= MIN_DEVICE_WIDTH.tablet
            ? el.closest(".store-locator__content")
            : el.closest(".store-locator__wrapper");
        if (!itemEl || !listItemsEl) return;
        const itemRect = itemEl.getBoundingClientRect();
        const listItemsRect = listItemsEl.getBoundingClientRect();
        return (
          itemRect.bottom > listItemsRect.top &&
          itemRect.top < listItemsRect.bottom &&
          itemRect.right > listItemsRect.left &&
          itemRect.left < listItemsRect.right
        );
      },

      toggleActive(id) {
        this.active = this.active === id ? null : id;
      },
    }));
  }

  function ensureStoreLocatorBound(root) {
    if (!root || !window.Alpine) return null;
    try {
      const existing = Alpine.$data(root);
      if (existing && typeof existing.openStoreLocatorPopup === "function") return existing;
    } catch (_) {}

    registerStoreLocator();
    try {
      // Re-bind if Alpine started before xStoreLocator was registered.
      Alpine.destroyTree?.(root);
      Alpine.initTree(root);
      return Alpine.$data(root);
    } catch (err) {
      console.error("[store-locator] ensureStoreLocatorBound failed", err);
      return null;
    }
  }

  function openA2ZStoreLocatorPopup() {
    const root = getStoreLocatorRoot();
    console.log("[store-locator] open attempt", {
      hasRoot: !!root,
      hasAlpine: !!window.Alpine,
      xDataAttr: root?.getAttribute("x-data"),
      sectionId: root?.dataset?.sectionId,
    });

    if (!root) {
      console.error("[store-locator] popup container not found in DOM");
      return false;
    }

    const data = ensureStoreLocatorBound(root);
    if (data && typeof data.openStoreLocatorPopup === "function") {
      data.openStoreLocatorPopup();
      console.log("[store-locator] opened via Alpine component");
      return true;
    }

    // Last resort: force visible without Alpine reactivity
    root.removeAttribute("x-cloak");
    root.style.setProperty("display", "flex", "important");
    root.style.pointerEvents = "auto";
    const dialog = document.getElementById("StoreLocatorPopup__Container");
    if (dialog) {
      dialog.removeAttribute("x-cloak");
      dialog.style.setProperty("display", "flex", "important");
    }
    console.warn("[store-locator] forced CSS open (Alpine bind missing)");
    return true;
  }

  document.addEventListener("click", (event) => {
    // Alpine header trigger already opens via xStoreMap — avoid double-open / rebind races.
    if (event.defaultPrevented) return;
    const trigger = event.target.closest("[data-open-store-locator]");
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    openA2ZStoreLocatorPopup();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const trigger = event.target.closest?.("[data-open-store-locator]");
    if (!trigger) return;
    event.preventDefault();
    openA2ZStoreLocatorPopup();
  });

  document.addEventListener("alpine:init", registerStoreLocator);

  // If this script loads after Alpine already started, register + rebind.
  if (window.Alpine) {
    registerStoreLocator();
    const root = getStoreLocatorRoot();
    if (root) ensureStoreLocatorBound(root);
  }

  document.addEventListener("alpine:initialized", () => {
    registerStoreLocator();
    const root = getStoreLocatorRoot();
    if (root) ensureStoreLocatorBound(root);
  });
}
